import { useEffect, useState } from "react";
import {
  adminGetProducts,
  adminCreateProduct,
  adminUpdateProduct,
  adminDeleteProduct,
  adminGetCategories,
  adminGetProductTypes,
} from "../../utils/api";
import "./AdminPage.css";

const EMPTY = {
  name: "",
  description: "",
  basePrice: "",
  categoryId: "",
  productTypeId: "",
  sizes: [],
  textures: [],
  images: [],
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState([]);
  const [error, setError] = useState("");
  const [primaryIndex, setPrimaryIndex] = useState(0);

  const selectedType = productTypes.find((t) => String(t.id) === String(form.productTypeId));
  const showSizes = selectedType?.hasSizes || false;
  const showTextures = selectedType?.hasTextures || false;

  const load = () => {
    setLoading(true);
    Promise.all([adminGetProducts(), adminGetCategories(), adminGetProductTypes()])
      .then(([p, c, t]) => {
        setProducts(p.data.data || []);
        setCategories(c.data.data || []);
        setProductTypes(t.data.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setPrimaryIndex(0);
    setEditing(null);
    setForm(EMPTY);
    setPreview([]);
    setError("");
    setModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setPrimaryIndex(0);
    setForm({
      name: p.name || "",
      description: p.description || "",
      basePrice: p.basePrice || "",
      categoryId: p.categoryId || "",
      productTypeId: p.productTypeId || "",
      sizes: p.sizes || [],
      textures: (p.textures || []).map((t) => ({
        ...t,
        imageFile: null, // Поле для зберігання нового файлу, якщо його виберуть
      })),
      images: p.images || [],
    });
    setPreview(p.images.map((i) => (i.startsWith("http") ? i : `${i}`)));
    setError("");
    setModal(true);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "images") {
      const filesArr = Array.from(files);
      setForm((f) => ({ ...f, images: filesArr }));
      const previews = filesArr.map((file) => URL.createObjectURL(file));
      setPreview(previews);
      return;
    }
    setForm((f) => ({ ...f, [name]: value }));
  };

  const addTexture = () => {
    setForm((f) => ({
      ...f,
      textures: [
        ...f.textures,
        {
          name: "",
          price: "",
          image: "", // Для існуючого шляху до файлу з бази
          imageFile: null, // Для нового об'єкта файлу з комп'ютера
        },
      ],
    }));
  };

  const updateTexture = (index, field, value) => {
    setForm((f) => {
      const updated = [...f.textures];
      updated[index] = { ...updated[index], [field]: value };
      return { ...f, textures: updated };
    });
  };

  const removeTexture = (index) => {
    setForm((f) => ({
      ...f,
      textures: f.textures.filter((_, i) => i !== index),
    }));
  };

  const addSize = () => {
    setForm((f) => ({
      ...f,
      sizes: [...f.sizes, { name: "", price: "" }],
    }));
  };

  const updateSize = (index, field, value) => {
    setForm((f) => {
      const updated = [...f.sizes];
      updated[index] = { ...updated[index], [field]: value };
      return { ...f, sizes: updated };
    });
  };

  const removeSize = (index) => {
    setForm((f) => ({
      ...f,
      sizes: f.sizes.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.basePrice) {
      setError("Назва та базова ціна обов'язкові");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("description", form.description);
      fd.append("basePrice", form.basePrice);

      if (form.categoryId) fd.append("categoryId", form.categoryId);
      if (form.productTypeId) fd.append("productTypeId", form.productTypeId);

      // 1. Розміри
      fd.append("sizes", JSON.stringify(form.sizes));

      // 2. Текстури + Мапінг нових файлів
      const texturesPayload = form.textures.map((texture, index) => {
        if (texture.imageFile instanceof File) {
          // Прив'язуємо файл до конкретного індексу текстури
          fd.append(`texture_file_${index}`, texture.imageFile);
        }
        return {
          name: texture.name,
          price: texture.price,
          image: texture.image || null, // Зберігаємо старий шлях, якщо нового файлу немає
        };
      });
      fd.append("textures", JSON.stringify(texturesPayload));

      // 3. Зображення товару
      if (editing) {
        // Відправляємо тільки старі текстові шляхи картинок
        const allImages = [...form.images];
if (primaryIndex !== 0) {
  const [main] = allImages.splice(primaryIndex, 1);
  allImages.unshift(main);
}
const existingStr = allImages.filter((img) => typeof img === "string");
allImages.forEach((img) => {
  if (img instanceof File) fd.append("images", img);
});
        fd.append("existingImages", JSON.stringify(existingStr));
      }

      const orderedImages = [...form.images];
if (primaryIndex !== 0) {
  const [main] = orderedImages.splice(primaryIndex, 1);
  orderedImages.unshift(main);
}

      if (editing) {
        await adminUpdateProduct(editing.id, fd);
      } else {
        await adminCreateProduct(fd);
      }

      setModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || "Помилка збереження");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Видалити цей товар?")) return;
    try {
      await adminDeleteProduct(id);
      load();
    } catch {
      alert("Помилка видалення");
    }
  };

  return (
    <div>
      <div className="admin-header">
        <h1>Товари</h1>
        <button className="admin-btn primary" onClick={openCreate}>
          + Додати товар
        </button>
      </div>

      {loading ? (
        <div className="spinner" />
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Фото</th>
                <th>Назва</th>
                <th>Категорія</th>
                <th>Тип</th>
                <th>Розміри</th>
                <th>Текстури</th>
                <th>Ціна</th>
                <th>Дата</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    style={{ textAlign: "center", padding: 40, color: "var(--text-light)" }}
                  >
                    Товарів немає
                  </td>
                </tr>
              ) : (
                products.map((p) => {
                  const imgUrl = p.images?.[0]
                    ? p.images[0].startsWith("http")
                      ? p.images[0]
                      : `${p.images[0]}`
                    : null;
                  const catName = p.categoryObj?.name || "—";
                  const typeName = p.productTypeObj?.name || "—";
                  const sizesArr = p.sizes || [];
                  const texturesArr = p.textures || [];
                  return (
                    <tr key={p.id}>
                      <td>
                        {imgUrl ? (
                          <img src={imgUrl} alt={p.name} className="admin-thumb" />
                        ) : (
                          <div className="admin-placeholder">✿</div>
                        )}
                      </td>
                      <td>
                        <strong>{p.name}</strong>
                      </td>
                      <td>{catName}</td>
                      <td>{typeName}</td>
                      <td>
                        {sizesArr.length > 0 ? (
                          <span style={{ fontSize: "0.78rem", color: "var(--text-light)" }}>
                            {sizesArr.map((s) => s.name).join(", ")}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {texturesArr.length > 0 ? (
                          <span style={{ fontSize: "0.78rem", color: "var(--text-light)" }}>
                            {texturesArr.map((t) => t.name).join(", ")}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <strong>{Number(p.basePrice).toFixed(0)} ₴</strong>
                      </td>
                      <td>{new Date(p.createdAt).toLocaleDateString("uk-UA")}</td>
                      <td>
                        <div className="admin-table-actions">
                          <button className="admin-btn secondary" onClick={() => openEdit(p)}>
                            Редагувати
                          </button>
                          <button className="admin-btn danger" onClick={() => handleDelete(p.id)}>
                            Видалити
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div
          className="admin-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModal(false);
          }}
        >
          <div className="admin-modal" style={{ maxWidth: 560 }}>
            <h2>{editing ? "Редагувати товар" : "Новий товар"}</h2>
            <form onSubmit={handleSubmit}>
              <div className="admin-form-group">
                <label>Назва *</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Назва товару"
                  required
                />
              </div>
              <div className="admin-form-group">
                <label>Опис</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Опис товару..."
                />
              </div>
              <div className="admin-form-group">
                <label>Ціна (₴) *</label>
                <input
                  name="basePrice"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.basePrice}
                  onChange={handleChange}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="admin-form-group">
                <label>Категорія</label>
                <select name="categoryId" value={form.categoryId} onChange={handleChange}>
                  <option value="">— Оберіть категорію —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="admin-form-group">
                <label>Тип товару</label>
                <select name="productTypeId" value={form.productTypeId} onChange={handleChange}>
                  <option value="">— Оберіть тип —</option>
                  {productTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                      {t.hasSizes ? " (з розмірами)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              {showSizes && (
                <div className="admin-form-group">
                  <div
                    style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}
                  >
                    <label>Розміри</label>
                    <button type="button" className="admin-btn secondary" onClick={addSize}>
                      + Додати розмір
                    </button>
                  </div>
                  {form.sizes.map((size, index) => (
                    <div
                      key={index}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 140px 60px",
                        gap: 10,
                        marginBottom: 10,
                      }}
                    >
                      <input
                        placeholder="Розмір"
                        value={size.name}
                        onChange={(e) => updateSize(index, "name", e.target.value)}
                      />
                      <input
                        type="number"
                        placeholder="Ціна"
                        value={size.price}
                        onChange={(e) => updateSize(index, "price", e.target.value)}
                      />
                      <button
                        type="button"
                        className="admin-btn danger"
                        onClick={() => removeSize(index)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {showTextures && (
                <div className="admin-form-group">
                  <div
                    style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}
                  >
                    <label>Текстури</label>
                    <button type="button" className="admin-btn secondary" onClick={addTexture}>
                      + Додати текстуру
                    </button>
                  </div>
                  {form.textures.map((texture, index) => (
                    <div
                      key={index}
                      style={{
                        border: "1px solid #eee",
                        padding: 12,
                        borderRadius: 12,
                        marginBottom: 12,
                      }}
                    >
                      <input
                        placeholder="Назва текстури"
                        value={texture.name}
                        onChange={(e) => updateTexture(index, "name", e.target.value)}
                        style={{ marginBottom: 8 }}
                      />
                      <input
                        type="number"
                        placeholder="Доплата"
                        value={texture.price}
                        onChange={(e) => updateTexture(index, "price", e.target.value)}
                        style={{ marginBottom: 8 }}
                      />

                      {texture.image && typeof texture.image === "string" && (
                        <div
                          style={{
                            marginBottom: 6,
                            fontSize: "0.8rem",
                            color: "var(--text-light)",
                          }}
                        >
                          Поточна:{" "}
                          <a
                            href={`${texture.image}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Переглянути
                          </a>
                        </div>
                      )}

                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => updateTexture(index, "imageFile", e.target.files[0])}
                      />
                      <button
                        type="button"
                        className="admin-btn danger"
                        style={{ marginTop: 10 }}
                        onClick={() => removeTexture(index)}
                      >
                        Видалити
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="admin-form-group">
                <label>Зображення товару</label>
                <input
                  name="images"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleChange}
                />
                {preview.length > 0 && (
  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
    {preview.map((src, i) => (
      <div
        key={i}
        onClick={() => setPrimaryIndex(i)}
        style={{
          position: "relative",
          cursor: "pointer",
          borderRadius: 8,
          border: i === primaryIndex ? "2px solid var(--terracotta)" : "2px solid transparent",
        }}
      >
        <img
          src={src}
          alt="preview"
          style={{ height: 80, width: 80, objectFit: "cover", borderRadius: 6, display: "block" }}
        />
        {i === primaryIndex && (
          <div style={{
            position: "absolute", bottom: 4, left: 0, right: 0,
            textAlign: "center", fontSize: "0.65rem",
            background: "var(--terracotta)", color: "#fff", borderRadius: "0 0 6px 6px",
          }}>
            головне
          </div>
        )}
      </div>
    ))}
  </div>
)}
              </div>

              {error && (
                <p style={{ color: "#dc2626", fontSize: "0.85rem", marginBottom: 8 }}>{error}</p>
              )}
              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-btn secondary"
                  onClick={() => setModal(false)}
                >
                  Скасувати
                </button>
                <button type="submit" className="admin-btn primary" disabled={submitting}>
                  {submitting ? "Збереження..." : editing ? "Зберегти" : "Створити"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
